/**
 * Per-install identifier for the account-less trial's abuse cap on the native
 * apps (2026-09-04). Pure helpers — no Capacitor import — so they are unit
 * testable; the storage glue lives in lib/installId.ts.
 *
 * The id is a random value generated on the device the first time a trial is
 * requested. The server stores only sha256(salt + id) for a rolling 24 h
 * per-install cap and nulls it after 7 days (backend/anon_stories.py). It is
 * never attached to any other request (lib/metrics.ts stays id-free) and never
 * logged. Format matches the server's ^[A-Za-z0-9_-]{16,64}$ validator.
 */
export const INSTALL_ID_RE = /^[A-Za-z0-9_-]{16,64}$/;

export function isValidInstallId(value: unknown): value is string {
  return typeof value === 'string' && INSTALL_ID_RE.test(value);
}

export function generateInstallId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(24);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
